import React, { useState, useEffect, useRef } from 'react';
import px from 'prop-types';

export default function ScrollIndicator({ 
    autoHide = false,
    className = '',
    hideDelay = 2000,
    itemClass = '',

    targetSelector = '',
}) {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const timeoutRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const container = document.querySelector(targetSelector);
        
        if (!container) return;

        containerRef.current = container;

        // Count total items
        const items = container.querySelectorAll(itemClass);

        setTotalItems(items.length);

        const handleScroll = () => {
            const scrollLeft = container.scrollLeft;
            const scrollWidth = container.scrollWidth;
            const clientWidth = container.clientWidth;
            const maxScroll = scrollWidth - clientWidth;
            
            if (maxScroll > 0) {
                const progress = (scrollLeft / maxScroll) * 100;

                setScrollProgress(Math.min(100, Math.max(0, progress)));
                
                // Calculate current item index
                const itemWidth = scrollWidth / items.length;
                const index = Math.round(scrollLeft / itemWidth);

                setCurrentIndex(Math.min(items.length - 1, Math.max(0, index)));
            }

            // Auto-hide functionality
            if (autoHide) {
                setIsVisible(true);
                if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(() => {
                setIsVisible(false);
                }, hideDelay);
            }
        };

        container.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial calculation

        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [targetSelector, autoHide, hideDelay, itemClass]);

    const scrollToIndex = (index) => {
        const container = containerRef.current;

        if (!container) return;

        const items = container.querySelectorAll(itemClass);

        if (items[index]) {
            const itemWidth = container.scrollWidth / items.length;
            const scrollLeft = itemWidth * index;

            container.scrollTo({
                left: scrollLeft,
                behavior: 'smooth'
            });
        }
    };

    const scrollToNext = () => {
        if (currentIndex < totalItems - 1) {
        scrollToIndex(currentIndex + 1);
        }
    };

    const scrollToPrev = () => {
        if (currentIndex > 0) {
        scrollToIndex(currentIndex - 1);
        }
    };

    if (totalItems <= 1) return null;

    return (
        <div 
            className={`scroll-indicator ${className}`}
            style={{
                opacity: isVisible ? 1 : 0.3,
                transition: 'opacity 0.3s ease',
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                background: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                margin: '16px auto',
                width: '100%',
                position: 'relative',
            }}
        >
            {/* Previous Button */}
            <button
                onClick={scrollToPrev}
                disabled={currentIndex === 0}
                style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                borderRadius: '50%',
                cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
                opacity: currentIndex === 0 ? 0.3 : 1,
                transition: 'opacity 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
                }}
                aria-label="Previous item"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6"></polyline>
                </svg>
            </button>

            {/* Progress Bar */}
            <div style={{
                flex: 1,
                height: '4px',
                backgroundColor: '#e5e7eb',
                borderRadius: '2px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                <div
                style={{
                    height: '100%',
                    backgroundColor: 'var(--primary)',
                    borderRadius: '2px',
                    width: `${scrollProgress}%`,
                    transition: 'width 0.1s ease'
                }}
                />
            </div>

            {/* Item Counter */}
            <div style={{
                fontSize: '12px',
                color: '#6b7280',
                fontWeight: '500',
                minWidth: '40px',
                textAlign: 'center'
            }}>
                {currentIndex + 1}/{totalItems}
            </div>

            {/* Next Button */}
            <button
                onClick={scrollToNext}
                disabled={currentIndex === totalItems - 1}
                style={{
                background: 'none',
                border: 'none',
                padding: '4px',
                borderRadius: '50%',
                cursor: currentIndex === totalItems - 1 ? 'not-allowed' : 'pointer',
                opacity: currentIndex === totalItems - 1 ? 0.3 : 1,
                transition: 'opacity 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
                }}
                aria-label="Next item"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9,18 15,12 9,6"></polyline>
                </svg>
            </button>
        </div>
    );
};

ScrollIndicator.propTypes = {
    autoHide: px.bool,
    className: px.string,
    hideDelay: px.number,
    itemClass: px.string,
    targetSelector: px.string,
}

